import { type ReactElement } from 'react';
import { redirect, useActionData, useLoaderData } from 'react-router';
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router';
import { type ProviderSubscription, type SubscriptionPlans } from '@marketplace/contracts';
import { localeOf } from '../features/auth/actions.js';
import { BillingPage } from '../features/billing/BillingPage.js';
import {
  isWatched,
  runBillingAction,
  type BillingActionData,
  type PlanKind,
} from '../features/billing/billing.js';
import { useBillingWatch } from '../features/billing/watch.js';
import { becomeAProPath } from '../features/payouts/onboarding.js';
import { ApiError } from '../shared/api.js';
import { queryKeys, routeContext } from '../shared/query.js';
import { loadSession } from '../shared/session.js';

/**
 * `/:lang/account/billing` — a provider's subscription: what their kind costs and **Subscribe**, or
 * what they are on, its status and date, **Manage billing** and **Switch**. `W5-T07` §4.10.
 *
 * **`?return=<from>` is Checkout's `success_url`** (and the portal's `return_url`). The webhook lags
 * the redirect, so the page shows the status it has and re-reads it every five seconds for a minute
 * while there is no subscription yet or its first payment is pending (§4.10). **`?cancelled=<from>`**
 * is Checkout's `cancel_url`: the page, with one line saying nothing was charged.
 *
 * **`?step=registration` is the subscription step of becoming a provider** (`W5-T13` §4.4, §4.6):
 * `become-a-pro` lands here, **Subscribe** starts Checkout from `registration`, and Checkout's return
 * and cancel come back with `registration` too. Once the subscription entitles, the page links on to
 * `W5-T01`'s step, Connect with Stripe.
 *
 * The status is our table's answer, never Stripe's (§4.1), and the price is the Stripe price's, read
 * through `GET /subscription/plans` (§4.2). Checkout and the portal are Stripe-hosted pages the action
 * redirects to; nothing keeps their URLs, and Stripe.js is not loaded (AC 38). Behind the same guard
 * as `/account/payouts`; an account with no provider profile is sent to `become-a-pro`.
 */
interface BillingData {
  locale: string;
  subscription: ProviderSubscription;
  kind: PlanKind;
  plans: SubscriptionPlans | null;
  watch: boolean;
  cancelled: boolean;
  /** `W5-T13` §4.6: the subscription step of becoming a provider, or Checkout's return to it. */
  registration: boolean;
}

export async function loader({
  params,
  request,
  context,
}: LoaderFunctionArgs): Promise<BillingData> {
  const locale = localeOf(params);

  const session = await loadSession(context);
  if (session === null) throw redirect(`/${locale}/login`);

  const search = new URL(request.url).searchParams;
  const { queryClient, api } = context.get(routeContext);

  let subscription: ProviderSubscription;
  try {
    // `staleTime: 0`: this page exists to show the latest answer, and the watch re-runs this loader.
    subscription = await queryClient.fetchQuery({
      queryKey: queryKeys.mySubscription(),
      queryFn: () => api.getMySubscription(),
      staleTime: 0,
    });
  } catch (error) {
    // `409`: a provider role with no profile; `403`: not a provider. Both are made one at
    // `become-a-pro`, as `/account/payouts` does.
    if (error instanceof ApiError && (error.status === 409 || error.status === 403)) {
      throw redirect(becomeAProPath(locale));
    }
    throw error;
  }

  // The profile's kind is what a new subscription is priced by, and what a mismatch names (§4.3).
  const own = await queryClient.fetchQuery({
    queryKey: queryKeys.myProviderProfile(),
    queryFn: () => api.getMyProviderProfile(),
  });
  if (own === null) throw redirect(becomeAProPath(locale));

  // A failure is "no price to show", never a broken page: the page says Stripe could not be reached
  // and sells nothing (`BillingPage`).
  const plans = await queryClient
    .fetchQuery({
      queryKey: queryKeys.subscriptionPlans(),
      queryFn: () => api.getSubscriptionPlans(),
    })
    .catch(() => null);

  return {
    locale,
    subscription,
    kind: own.kind,
    plans,
    watch: search.has('return'),
    cancelled: search.has('cancelled'),
    registration: ['step', 'return', 'cancelled'].some((key) => search.get(key) === 'registration'),
  };
}

export async function action({
  params,
  request,
  context,
}: ActionFunctionArgs): Promise<BillingActionData | Response> {
  return runBillingAction(context, localeOf(params), await request.formData());
}

export function Component(): ReactElement {
  const data = useLoaderData<BillingData>();
  const answer = useActionData<BillingActionData>();
  const failure = answer !== undefined && 'failure' in answer ? answer.failure : null;

  const watching = data.watch && isWatched(data.subscription.status);
  const open = useBillingWatch(watching);

  return (
    <BillingPage
      locale={data.locale}
      subscription={data.subscription}
      kind={data.kind}
      plans={data.plans}
      failure={failure}
      watching={watching}
      pending={watching && !open}
      cancelled={data.cancelled}
      registration={data.registration}
    />
  );
}
